/**
 * The documentation's Markdown, as blocks: headings, paragraphs, lists and fenced code.
 *
 * Kept apart from the component that draws them, so the parser can be imported without React and
 * the component file exports only a component. See `Markdown.tsx` for what is supported and why.
 */

export type Block =
  | { type: 'heading'; level: 2 | 3; text: string }
  | { type: 'paragraph'; text: string }
  | { type: 'list'; ordered: boolean; items: string[] }
  | { type: 'code'; text: string };

/** The anchor a heading gets, so `#connecting-a-server` in a link finds it. */
export function headingId(text: string): string {
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-');
}

export function parseBlocks(markdown: string): Block[] {
  const lines = markdown.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let paragraph: string[] = [];
  let list: { ordered: boolean; items: string[] } | null = null;

  const flush = (): void => {
    if (paragraph.length > 0) {
      blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
      paragraph = [];
    }

    if (list !== null) {
      blocks.push({ type: 'list', ...list });
      list = null;
    }
  };

  for (let index = 0; index < lines.length; index++) {
    const line = lines[index] ?? '';

    if (line.startsWith('```')) {
      flush();
      const code: string[] = [];
      index++;

      while (index < lines.length && !(lines[index] ?? '').startsWith('```')) {
        code.push(lines[index] ?? '');
        index++;
      }

      blocks.push({ type: 'code', text: code.join('\n') });
      continue;
    }

    const heading = /^(#{2,3})\s+(.*)$/.exec(line);

    if (heading !== null) {
      flush();
      blocks.push({ type: 'heading', level: heading[1] === '##' ? 2 : 3, text: (heading[2] ?? '').trim() });
      continue;
    }

    const bullet = /^\s*-\s+(.*)$/.exec(line);
    const numbered = /^\s*\d+\.\s+(.*)$/.exec(line);

    if (bullet !== null || numbered !== null) {
      const ordered = numbered !== null;

      if (paragraph.length > 0) {
        blocks.push({ type: 'paragraph', text: paragraph.join(' ') });
        paragraph = [];
      }

      if (list === null || list.ordered !== ordered) {
        if (list !== null) {
          blocks.push({ type: 'list', ...list });
        }
        list = { ordered, items: [] };
      }

      list.items.push(((bullet ?? numbered)?.[1] ?? '').trim());
      continue;
    }

    if (line.trim() === '') {
      flush();
      continue;
    }

    // A line indented under a list item continues it.
    if (list !== null && /^\s{2,}\S/.test(line)) {
      list.items[list.items.length - 1] += ' ' + line.trim();
      continue;
    }

    if (list !== null) {
      blocks.push({ type: 'list', ...list });
      list = null;
    }

    paragraph.push(line.trim());
  }

  flush();

  return blocks;
}
